import { useEffect, useMemo, useState } from "react";
import type {
  OpportunityListItem,
  OpportunityListPageSize,
  OpportunityListSortDir,
  OpportunityListSortKey,
} from "@/lib/opportunity/opportunityListTypes";
import {
  OPPORTUNITY_LIST_DEFAULT_PAGE_SIZE,
  OPPORTUNITY_LIST_DEFAULT_SORT_DIR,
  OPPORTUNITY_LIST_DEFAULT_SORT_KEY,
} from "@/lib/opportunity/opportunityListTypes";

function compareNullableText(a: string | null | undefined, b: string | null | undefined): number {
  const left = (a ?? "").trim().toLowerCase();
  const right = (b ?? "").trim().toLowerCase();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right);
}

function sortOpportunities(
  items: OpportunityListItem[],
  sortKey: OpportunityListSortKey,
  sortDir: OpportunityListSortDir,
): OpportunityListItem[] {
  const dir = sortDir === "asc" ? 1 : -1;
  const sorted = [...items];
  sorted.sort((a, b) => {
    let cmp = 0;
    if (sortKey === "updated") {
      const aTs = Date.parse(a.updated_at ?? "");
      const bTs = Date.parse(b.updated_at ?? "");
      cmp = (Number.isFinite(aTs) ? aTs : 0) - (Number.isFinite(bTs) ? bTs : 0);
    } else if (sortKey === "user") {
      cmp = compareNullableText(a.list_summary?.assigned_user_name, b.list_summary?.assigned_user_name);
    } else {
      cmp = compareNullableText(a.list_summary?.location_name, b.list_summary?.location_name);
    }
    return cmp * dir;
  });
  return sorted;
}

export type OpportunityListTableState = {
  sortKey: OpportunityListSortKey;
  sortDir: OpportunityListSortDir;
  page: number;
  pageSize: OpportunityListPageSize;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  pageItems: OpportunityListItem[];
  setSortKey: (key: OpportunityListSortKey) => void;
  toggleSortDir: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: OpportunityListPageSize) => void;
};

export function useOpportunityListTable(items: OpportunityListItem[]): OpportunityListTableState {
  const [sortKey, setSortKeyState] = useState<OpportunityListSortKey>(
    OPPORTUNITY_LIST_DEFAULT_SORT_KEY,
  );
  const [sortDir, setSortDir] = useState<OpportunityListSortDir>(OPPORTUNITY_LIST_DEFAULT_SORT_DIR);
  const [pageSize, setPageSizeState] = useState<OpportunityListPageSize>(
    OPPORTUNITY_LIST_DEFAULT_PAGE_SIZE,
  );
  const [page, setPage] = useState(0);

  const sorted = useMemo(
    () => sortOpportunities(items, sortKey, sortDir),
    [items, sortKey, sortDir],
  );

  useEffect(() => {
    if (page > 0 && page * pageSize >= sorted.length) setPage(0);
  }, [sorted.length, page, pageSize]);

  function setSortKey(key: OpportunityListSortKey) {
    setSortKeyState((prev) => {
      if (prev === key) return prev;
      setPage(0);
      return key;
    });
  }

  function toggleSortDir() {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(0);
  }

  function setPageSize(next: OpportunityListPageSize) {
    setPageSizeState(next);
    setPage(0);
  }

  const pageCount = sorted.length > 0 ? Math.ceil(sorted.length / pageSize) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const rangeStart = sorted.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, sorted.length);
  const pageItems = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return {
    sortKey,
    sortDir,
    page: safePage,
    pageSize,
    pageCount,
    rangeStart,
    rangeEnd,
    pageItems,
    setSortKey,
    toggleSortDir,
    setPage,
    setPageSize,
  };
}
